import { ActionContext } from 'vuex';

import api from '@/api';
import { ApiGroupsList, ApiGroupsListItem } from '@/api/types/groups';
import { Sorting, SortingDir } from '@/types/sorting';

interface GroupsState {
    list: ApiGroupsList;
    listRequestPending: boolean;
    listSorting: Sorting;
    listSortingDir: SortingDir;
    listSortingOptions: Sorting[];
    listSearchFilter: string;
}

const groups = {
    namespaced: true,
    state: {
        list: {},
        listRequestPending: false,
        listSorting: Sorting.NAME,
        listSortingDir: SortingDir.ASC,
        listSortingOptions: [Sorting.NAME],
        listSearchFilter: '',
    },
    getters: {
        items(state: GroupsState): Array<ApiGroupsList> {
            if (typeof state.list.List === 'undefined' || !state.list.List || state.list.List.length === 0) {
                return [];
            }

            const items = JSON.parse(JSON.stringify(state.list.List));

            items.sort((a: ApiGroupsListItem, b: ApiGroupsListItem) => {
                const dirModifier = state.listSortingDir === SortingDir.DESC ? -1 : 1;

                if (
                    state.listSorting === Sorting.CODE &&
                    Object.prototype.hasOwnProperty.call(a, 'Code') &&
                    Object.prototype.hasOwnProperty.call(b, 'Code')
                ) {
                    return (a.Code > b.Code ? 1 : b.Code > a.Code ? -1 : 0) * dirModifier;
                }
                return (a.Name > b.Name ? 1 : b.Name > a.Name ? -1 : 0) * dirModifier;
            });
            return items.filter((item: ApiGroupsListItem) =>
                item.Name?.toLocaleLowerCase().includes(state.listSearchFilter?.toLocaleLowerCase()),
            );
        },
        groups(state: GroupsState): Array<ApiGroupsListItem> {
            return (
                state.list?.List?.map((groupe) => ({
                    Id: groupe.Id,
                    Name: groupe.Name,
                    Code: groupe.Code,
                    Color: groupe.Color,
                })) || []
            );
        },
    },
    mutations: {
        startListRequest(state: GroupsState): void {
            state.listRequestPending = true;
        },
        endListRequest(state: GroupsState): void {
            state.listRequestPending = false;
        },
        updateList(state: GroupsState, list: ApiGroupsList): void {
            state.list = list;
        },
        updateSortingOptions(state: GroupsState, list: Sorting[]): void {
            state.listSortingOptions = list;
            state.listSorting = list[0];
        },
        clearList(state: GroupsState): void {
            state.list = {};
        },
        clearFilters(state: GroupsState): void {
            state.listRequestPending = false;
            state.listSorting = Sorting.NAME;
            state.listSortingDir = SortingDir.ASC;
            state.listSortingOptions = [Sorting.NAME];
            state.listSearchFilter = '';
        },
        updateListSorting(state: GroupsState, sort: Sorting): void {
            state.listSorting = sort;
        },
        updateListSortingDir(state: GroupsState, dir: SortingDir): void {
            state.listSortingDir = dir;
        },
        updateListSearchFilter(state: GroupsState, searchPhrase: string): void {
            state.listSearchFilter = searchPhrase;
        },
    },
    actions: {
        clearList(context: ActionContext<GroupsState, null>): void {
            context.commit('clearList');
        },
        clearFilters(context: ActionContext<GroupsState, null>): void {
            context.commit('clearFilters');
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        async list(context: ActionContext<GroupsState, null>, translate: any): Promise<void> {
            context.commit('startListRequest');
            try {
                const token = window.localStorage.getItem('authcredentials');
                const response = await api.groups.list(token || '');
                // eslint-disable-next-line no-unused-expressions
                response.data?.List
                    ? response.data?.List.forEach((elem) => {
                          const isCode = Object.prototype.hasOwnProperty.call(elem, 'Code');
                          if (isCode) {
                              context.commit('updateSortingOptions', [Sorting.CODE, Sorting.NAME]);
                          }
                      })
                    : [];

                // create special group for devices without groups
                if (response.data?.List && response.data?.List.length > 0) {
                    response.data.List = [
                        ...response.data.List,
                        {
                            Id: -1,
                            Name: translate('components.reportExportModal.devicesFilters.emptyGroup.name'),
                            Code: '',
                            Color: '#000',
                        },
                    ];
                }

                context.commit('updateList', response.data);
            } catch (err) {
                // console.log(err);
            } finally {
                context.commit('endListRequest');
            }
        },
    },
};

export default groups;
